

// src/components/helpers/initialiseLayers.js

// This helper function initialises the map layers based on fetched data and URL parameters.
// It updates state and adds the relevant layers to the map.

import { replaceUrlState } from './urlUpdaterHelper.js';

export async function initialiseLayers({
  fetchMapLayers,
  mapRef,
  topLayerRef,
  setLayers,
  setSelectedBaseMap,
  setIsParcelBoundariesVisible
}) {

  //console.log('Fetching layers...');

  const urlParams = new URLSearchParams(window.location.search);
  const baseMapIndexParam = parseInt(urlParams.get('bm'));
  const parcelBoundariesParam = parseInt(urlParams.get('pb'));

  const layers = await fetchMapLayers(); // Fetch layers from server
  setLayers(layers);  // Store fetched layers in the component's state

  // Init parcel boundaries visibility based on argument in the URL, or on by default if nothing.
  if (urlParams.has('pb')) {
    // Respect the user's setting
    setIsParcelBoundariesVisible(parcelBoundariesParam);
  } else {
    // Default to ON and write it to the URL
    setIsParcelBoundariesVisible(true);
    const url = new URL(window.location);
    url.searchParams.set('pb', '1');
    replaceUrlState(url);
  }

  // If there are secondary layers available, set the first one as the default base map
  if (Array.isArray(layers.secondary) && layers.secondary.length > 0) {

    let selectedBaseMapName;

    if (!isNaN(baseMapIndexParam)) {

      if (baseMapIndexParam === -1) {
        selectedBaseMapName = 'None'; // Special handling for "None"
      }
      else if (layers.secondary[baseMapIndexParam]) {
        selectedBaseMapName = layers.secondary[baseMapIndexParam].get('name');
      }
    }
    else {  // Set the default basemap that shows on launch if there is no argument in the URL.
      selectedBaseMapName = layers.secondary[0].get('name');  // Default to the LINZ basemap if there is no argument in the URL.
      //selectedBaseMapName = 'None';  // Default to our custom road map if there is no argument in the URL.
    }

    setSelectedBaseMap(selectedBaseMapName);

    if (selectedBaseMapName !== 'None') {
      const defaultLayer = layers.secondary.find(
        layer => layer.get('name') === selectedBaseMapName
      );

      // If the selected base map layer (like LINZ aerial or NZTopo) is a custom image-based map layer
      // (not a built-in Google map type like "satellite" or "roadmap"), and it has a format property
      // that provides a tile URL template, then we’ll create and register a custom tile layer using ImageMapType.
      if (defaultLayer && defaultLayer.get('format')) {

        const secondaryMapType = new window.google.maps.ImageMapType({

          getTileUrl: function (coord, zoom) {
            // Replace placeholders in the URL with actual coordinates and zoom level
            const url = defaultLayer.get('format')
              .replace('{z}', zoom)
              .replace('{x}', coord.x)
              .replace('{y}', coord.y);
            return url;
          },
          tileSize: new window.google.maps.Size(256, 256),
          maxZoom: 22,
          name: defaultLayer.get('name')
        });

        if (mapRef.current && mapRef.current.overlayMapTypes) {
          mapRef.current.overlayMapTypes.push(secondaryMapType);
        }
      }
    }
  }

  // Initialize the parcel boundaries layer using the first primary layer
  const firstPrimaryLayer = Array.isArray(layers.primary) ? layers.primary[0] : null;

  if (firstPrimaryLayer && firstPrimaryLayer.get('format')) {

    topLayerRef.current = new window.google.maps.ImageMapType({
      getTileUrl: function (coord, zoom) {
        return firstPrimaryLayer.get('format')
          .replace('{z}', zoom)
          .replace('{x}', coord.x)
          .replace('{y}', coord.y);
      },
      tileSize: new window.google.maps.Size(256, 256),
      maxZoom: 22,
      name: firstPrimaryLayer.get('name')
    });

    // Check if the URL has a parameter to show or hide parcel boundaries. If so respect it, if not default to on.
    const showParcels = urlParams.has('pb') ? parcelBoundariesParam === 1 : true;

    if (showParcels) {
      if (mapRef.current && mapRef.current.overlayMapTypes) {
        mapRef.current.overlayMapTypes.push(topLayerRef.current);
      }
    }

    setIsParcelBoundariesVisible(showParcels);
  }
}

